import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Card from 'apollo-react/components/Card';
import Divider from 'apollo-react/components/Divider';
import TextField from 'apollo-react/components/TextField';
import CloseCircle from 'apollo-react-icons/CloseCircle';
import Search from 'apollo-react-icons/Search';
import React, { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { SearchPanel } from './SearchPanel';
import {
  clearHelpTopicsError,
  clearSearchHistory,
  getAnswers,
  setCurrentQuestion,
} from '../../../actions/HelpTopicsActions';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../../custom-hooks/useWindowDimensions';
import {
  getCurrentQuestion,
  getQuestionsAndAnswers,
  hasHelpTopicsError,
} from '../../../selectors/HelpTopicsSelectors';

const useStyles = makeStyles({
  mainContainer: {
    display: 'flex',
  },
  mainContainerMobile: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  inputSearchContainer: {
    position: 'relative',
    flex: 1,
  },
  inputQuestion: {
    margin: 0,
    flex: '3 1 0',
  },
  searchContainer: {
    zIndex: 999,
    width: '100%',
    position: 'absolute',
    marginTop: 3,
    padding: '0px 0px 7px 7px',
  },
  searchActionsContainer: {
    display: 'flex',
    marginTop: 7,
    justifyContent: 'flex-end',
  },
  askQuestionButton: {
    marginLeft: 20,
    padding: '0 30px',
  },
  askQuestionButtonMobile: {
    marginTop: 10,
    padding: '0 30px',
    width: '100%',
  },
});

export const SearchSection = (props) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const searchRef = useRef();

  const { questionValue, setQuestionValue, latestAskedQuestion, isLoading, setLoading } = props;

  const currentQuestion = useSelector(getCurrentQuestion);
  const questionsAndAnswers = useSelector(getQuestionsAndAnswers);
  const isHelpTopicsError = useSelector(hasHelpTopicsError);

  const [isSearchOpen, setOpenSearch] = useState(false);
  const [filteredSearchQuestion, setFilteredSearchQuestion] = useState([]);

  const { isMobileDevice } = useWindowDimensions();

  const getSearchedQuestion = (value) => {
    return questionsAndAnswers.find(
      ({ question }) => question.toLowerCase() === value.toLowerCase().trim()
    );
  };

  const handleSearch = async () => {
    if (!questionValue || questionValue.trim() === currentQuestion?.question) {
      return;
    }
    setOpenSearch(false);

    const searchedQuestion = getSearchedQuestion(questionValue);

    if (searchedQuestion) {
      return dispatch(setCurrentQuestion(searchedQuestion.id));
    }

    setLoading(true);
    await dispatch(getAnswers(questionValue));
    setLoading(false);
  };

  const handleResetSearch = () => {
    setQuestionValue('');
    dispatch(setCurrentQuestion(null));
  };

  const handleInput = ({ target }) => {
    if (!target.value) {
      dispatch(setCurrentQuestion(null));
    }
    setQuestionValue(target.value);
  };

  const handleQuestionChoose = (question, id) => {
    setQuestionValue(question);
    batch(() => {
      dispatch(setCurrentQuestion(id));
      dispatch(clearHelpTopicsError);
    });
    setOpenSearch(false);
  };

  const handleKeyPress = ({ key }) => {
    if (key === 'Enter') {
      handleSearch();
    }
  };

  const getFilteredSearch = () => {
    return questionsAndAnswers.reduce((acc, cur) => {
      if (cur.question.toLowerCase().includes(questionValue.toLowerCase().trim())) {
        acc.push(cur);
      }
      return acc;
    }, []);
  };

  const handleClearSearchHistory = () => {
    dispatch(clearSearchHistory());
    localStorage.removeItem('questionsAndAnswers');
  };

  useEffect(() => {
    if (isHelpTopicsError) {
      setQuestionValue('');
    }
  }, [setQuestionValue, isHelpTopicsError]);

  useEffect(() => {
    setFilteredSearchQuestion(getFilteredSearch());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionValue, questionsAndAnswers.length]);

  useEffect(() => {
    const checkOutsideSearchClick = ({ target }) => {
      if (isSearchOpen && searchRef.current && !searchRef.current.contains(target)) {
        setOpenSearch(false);
      }
    };
    document.addEventListener('click', checkOutsideSearchClick);

    return () => {
      document.removeEventListener('click', checkOutsideSearchClick);
    };
  }, [isSearchOpen]);

  const isCloseAction = questionValue && questionValue.trim() === currentQuestion?.question;

  return (
    <div className={isMobileDevice ? classes.mainContainerMobile : classes.mainContainer}>
      <div className={classes.inputSearchContainer} ref={searchRef}>
        <TextField
          className={classes.inputQuestion}
          placeholder={translateString('Ask a question')}
          icon={isCloseAction ? <CloseCircle /> : <Search color="primary" />}
          InputLabelProps={{
            style: { marginTop: -5 },
          }}
          onFocus={() => setOpenSearch(true)}
          disabled={isLoading}
          fullWidth
          onKeyDown={handleKeyPress}
          onChange={handleInput}
          iconProps={{
            onClick: isCloseAction ? handleResetSearch : handleSearch,
          }}
          value={questionValue}
        />
        {isSearchOpen && (
          <Card className={classes.searchContainer}>
            <SearchPanel
              questions={questionValue ? filteredSearchQuestion : latestAskedQuestion}
              questionValue={questionValue}
              handleQuestionChoose={handleQuestionChoose}
            />
            <div style={{ marginRight: 7 }}>
              <Divider />
              <div className={classes.searchActionsContainer}>
                <Button
                  disabled={questionsAndAnswers.length === 0}
                  variant="text"
                  size="small"
                  onClick={handleClearSearchHistory}
                >
                  {translateString('Clear history')}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
      <Button
        variant="primary"
        onClick={handleResetSearch}
        disabled={!currentQuestion || !questionValue || isLoading}
        className={isMobileDevice ? classes.askQuestionButtonMobile : classes.askQuestionButton}
      >
        {translateString('Ask another question')}
      </Button>
    </div>
  );
};
